import App from './App'
import Document from './Document'
import { StrictMode } from 'react'
import { initFirebase } from 'shared/firebase-config'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { initAnalytics } from 'shared/analytics'
import { analyticsAppForPath } from 'lib/analyticsApp'
import { syncAnalyticsConsent } from 'lib/cookieConsent'
import { isChunkLoadError, reloadForStaleChunk } from 'lib/staleChunkReload'

// Safety net for a lazy import with no error boundary above it: React has already
// torn the tree down, so reloading onto the current build is the only recovery.
const rootOptions = {
  onUncaughtError: (error) => {
    console.error(error)
    if (isChunkLoadError(error)) reloadForStaleChunk()
  },
}

/**
 * Client entry point.
 *
 * SSR mode  → hydrateRoot(document, ...)
 *   React takes ownership of the full document (html/head/body). This is
 *   required for React 19's metadata hoisting to work: title/meta/link tags
 *   rendered anywhere in the tree land in <head>, matching what the server
 *   produced via entry-server.jsx + Document.jsx.
 *
 * SPA mode  → createRoot(#root, ...)
 *   No server HTML; React mounts fresh into #root. React 19 hoists
 *   title/meta/link into document.head client-side — no Document wrapper needed.
 */

// initializeAuth() is first-writer-wins for persistence across the app.
initFirebase()

// Browser-only initialization. Must NOT live in App.jsx because the server
// entry imports App.jsx too, and Mixpanel touches `window` at init.
//
// One instance for the whole page (remotes use it via host/shared). It starts
// opted out; the cookie consent decides (CookieBar / syncAnalyticsConsent).
// `app` is resolved per event from the URL so remote events keep their label.
// apiHost: this domain's own proxy instead of api-eu.mixpanel.com.
const mixpanelToken = import.meta.env.VITE_MIXPANEL_TOKEN?.trim()
const websiteDomain = import.meta.env.VITE_WEBSITE_DOMAIN
if (mixpanelToken) {
  initAnalytics({
    token: mixpanelToken,
    app: 'homepage',
    apiHost: websiteDomain ? `https://analytics.${websiteDomain}` : undefined,
    requireConsent: true,
    resolveApp: () => analyticsAppForPath(window.location.pathname),
  })
  syncAnalyticsConsent()
}

async function enableMocking() {
  if (import.meta.env.DEV) return
  if (!import.meta.env.VITE_ENABLE_MSW?.startsWith('true')) return

  const { worker } = await import('./mocks/browser')

  // `worker.start()` returns a Promise that resolves
  // once the Service Worker is up and ready to intercept requests.
  return worker.start()
}

const container = document.getElementById('root')
// Read SSR seed before deleting. ssrData is passed to Document so the
// client renders window.__SSR_DATA__=<same-json> as the server did, giving
// React an exact match and no hydration warning on that script element.
const ssrData = (typeof window !== 'undefined' && window.__SSR_DATA__) || {}
if (typeof window !== 'undefined') {
  delete window.__SSR_DATA__
}

// `firstElementChild` (rather than `hasChildNodes()`) so HTML comments don't
// false-positive as "SSR happened".
if (container.firstElementChild) {
  enableMocking().then(() => {
    // Server rendered the full document — hydrate it.
    hydrateRoot(
      document,
      <StrictMode>
        <Document ssrData={ssrData}>
          <App ssrData={ssrData} />
        </Document>
      </StrictMode>,
      rootOptions,
    )
  })
} else {
  // No SSR content — mount fresh into #root (SPA / fallback mode).
  enableMocking().then(() => {
    createRoot(container, rootOptions).render(
      <StrictMode>
        <App ssrData={ssrData} />
      </StrictMode>,
    )
  })
}
